import React, { useEffect, useRef, useState } from 'react';
import { Check, Search } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { obtenerIniciales } from '../utils/format';

// Colores del círculo de cada empresa: fijos por id, para que una empresa
// se vea siempre igual. Las empresas no tienen logo guardado; si algún día
// lo tienen, va aquí en lugar de las iniciales.
const COLORES = [
  'bg-turquoise-600 text-white',
  'bg-indigo-600 text-white',
  'bg-amber-500 text-white',
  'bg-rose-600 text-white',
  'bg-emerald-600 text-white',
  'bg-sky-600 text-white',
  'bg-violet-600 text-white',
  'bg-orange-600 text-white',
  'bg-slate-700 text-white',
  'bg-lime-600 text-white',
];

const normalizar = (t) => String(t || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

const RUBROS = { restaurante: 'Restaurante', fabrica: 'Fábrica', importadora: 'Importadora', personal: 'Personal' };

export function AvatarEmpresa({ empresa, tamano = 'h-8 w-8 text-xs' }) {
  const color = COLORES[Math.abs(Number(empresa?.empresa_id) || 0) % COLORES.length];
  return (
    <span
      aria-hidden="true"
      className={`flex flex-shrink-0 items-center justify-center rounded-full font-semibold ${tamano} ${color}`}
    >
      {obtenerIniciales(empresa?.empresa_nombre)}
    </span>
  );
}

// Selector de empresa de la barra superior: un círculo con las iniciales de
// la empresa activa (como la foto del usuario); al pulsarlo se abre la
// lista con el círculo de cada empresa.
export default function SelectorEmpresa() {
  const { empresas, empresaActiva, seleccionarEmpresa } = useAuth();
  const [abierta, setAbierta] = useState(false);
  const [filtro, setFiltro] = useState('');
  const caja = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const activa = empresas.find((e) => String(e.empresa_id) === String(empresaActiva));

  useEffect(() => {
    if (!abierta) return undefined;
    const fuera = (e) => { if (caja.current && !caja.current.contains(e.target)) setAbierta(false); };
    const esc = (e) => { if (e.key === 'Escape') setAbierta(false); };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', fuera); document.removeEventListener('keydown', esc); };
  }, [abierta]);

  useEffect(() => { if (!abierta) setFiltro(''); }, [abierta]);

  // Una fila sin empresa (admin cargado con empresa_id nulo) no se puede elegir.
  const elegibles = empresas.filter((e) => e.empresa_id != null);
  const conFiltro = elegibles.length > 6;
  const visibles = filtro
    ? elegibles.filter((e) => normalizar(e.empresa_nombre).includes(normalizar(filtro.trim())))
    : elegibles;

  function elegir(id) {
    seleccionarEmpresa(id);
    setAbierta(false);
    // Marca la página actual como "recién cambiada de empresa": si la nueva
    // empresa no tiene ese módulo (o el rol no alcanza), ProtectedRoute manda
    // al inicio en vez de mostrar el aviso. Un enlace directo no trae la marca.
    if (String(id) !== String(empresaActiva)) {
      navigate(
        { pathname: location.pathname, search: location.search, hash: location.hash },
        { replace: true, state: { ...(location.state || {}), empresaCambiada: true } }
      );
    }
  }

  return (
    <div ref={caja} className="relative flex-shrink-0">
      <button
        onClick={() => setAbierta((a) => !a)}
        title={activa ? `${activa.empresa_nombre} · cambiar de empresa` : 'Elegir empresa'}
        aria-haspopup="listbox"
        aria-expanded={abierta}
        className="flex items-center gap-2 rounded-full p-0.5 pr-0.5 lg:pr-3 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-turquoise-500/60"
      >
        <AvatarEmpresa empresa={activa} tamano="h-9 w-9 text-sm" />
        <span className="hidden lg:block max-w-[150px] truncate text-sm font-medium text-slate-700">
          {activa?.empresa_nombre}
        </span>
      </button>

      {abierta && (
        <div className="fixed left-3 right-3 top-16 sm:absolute sm:left-0 sm:right-auto sm:top-full sm:mt-2 sm:w-80 z-50 rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden">
          <p className="border-b border-slate-100 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Cambiar de empresa
          </p>
          {conFiltro && (
            <div className="border-b border-slate-100 px-3 py-2">
              <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-2.5 py-1.5 focus-within:ring-2 focus-within:ring-turquoise-500/40">
                <Search size={14} className="text-slate-400" />
                <input
                  autoFocus
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  placeholder="Buscar empresa…"
                  className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm focus:outline-none focus:ring-0"
                />
              </label>
            </div>
          )}
          <ul role="listbox" className="max-h-[60vh] overflow-y-auto py-1">
            {visibles.map((e) => {
              const esActiva = String(e.empresa_id) === String(empresaActiva);
              return (
                <li key={e.empresa_id}>
                  <button
                    role="option"
                    aria-selected={esActiva}
                    onClick={() => elegir(e.empresa_id)}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-slate-50 ${esActiva ? 'bg-turquoise-50/60' : ''}`}
                  >
                    <AvatarEmpresa empresa={e} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-700">{e.empresa_nombre}</span>
                      <span className="block truncate text-xs text-slate-400">
                        {[RUBROS[e.rubro], e.rol && e.rol[0].toUpperCase() + e.rol.slice(1)].filter(Boolean).join(' · ')}
                      </span>
                    </span>
                    {esActiva && <Check size={16} className="flex-shrink-0 text-turquoise-600" />}
                  </button>
                </li>
              );
            })}
            {visibles.length === 0 && <li className="px-4 py-3 text-sm text-slate-400">Ninguna empresa con ese nombre.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
